import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import AppToolTip from '../InputBox/AppToolTip';
import { colors, borders } from '../../Tokens/Colors';
import { borderRadius, fontSizes, fontWeights, spacing } from '../../Tokens/Scales';
import { sizes } from '../../Tokens/Sizes';

const SEGMENT_HEIGHT = 28;

const groupStyles = {
  p: '2px',
  gap: '2px',
  border: borders.default,
  borderRadius: `${borderRadius.lg}px`,
  backgroundColor: colors.background.page,
  '& .MuiToggleButtonGroup-grouped': {
    // MUI joins grouped buttons by stripping inner borders/radii; a segmented
    // control has its own frame, so every segment is borderless and rounded.
    border: 0,
    borderRadius: `${borderRadius.md}px !important`,
    margin: 0,
  },
};

const segmentStyles = {
  height: SEGMENT_HEIGHT,
  px: `${spacing.sm + 2}px`,
  gap: `${spacing.xs + 2}px`,
  textTransform: 'none',
  fontSize: fontSizes.sm,
  fontWeight: fontWeights.medium,
  lineHeight: 1,
  color: colors.text.secondary,
  transition: 'background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
  '& .MuiSvgIcon-root': { fontSize: sizes.icon.xsmall },
  '&:hover': {
    backgroundColor: colors.border.focusTint08,
    color: colors.text.primary,
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: colors.background.surface,
    color: colors.border.focus,
    fontWeight: fontWeights.semibold,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08), 0 0 0 1px rgba(15, 23, 42, 0.04)',
  },
  '&.Mui-disabled': { color: colors.text.disabled },
};

/**
 * AppViewSwitcher — a compact group of mutually exclusive options, for
 * switching between peer views of the same thing. Exactly one option is always
 * selected; clicking the active one does nothing.
 *
 * Use for: HTML / PDF preview, Grid / Card list view, Chart / Table,
 * Summary / Detail, Monthly / Quarterly / Yearly periods.
 * Not for: an on/off setting (AppSwitch), page sections (AppTabs), or more
 * than ~4 options (AppSingleSelect).
 *
 * @param {Array<{ value: string, label?: node, icon?: elementType|element, tooltip?: node, disabled?: boolean }>} options
 * @param {string}   value      Selected option value
 * @param {function} onChange   Called with the newly selected value
 * @param {string}   ariaLabel  Accessible name for the group
 */
const AppViewSwitcher = ({ options, value, onChange, ariaLabel, disabled = false, sx, ...props }) => (
  <ToggleButtonGroup
    exclusive
    value={value}
    disabled={disabled}
    onChange={(_, next) => {
      if (next !== null && next !== undefined) onChange?.(next);
    }}
    aria-label={ariaLabel}
    sx={sx ? [groupStyles, sx] : groupStyles}
    {...props}
  >
    {options.map(({ value: optionValue, label, icon: Icon, tooltip, disabled: optionDisabled }) => {
      const iconNode = React.isValidElement(Icon) ? Icon : Icon ? <Icon /> : null;
      return (
        <ToggleButton
          key={optionValue}
          value={optionValue}
          disabled={optionDisabled}
          aria-label={typeof label === 'string' ? label : optionValue}
          sx={segmentStyles}
        >
          <AppToolTip title={tooltip || ''}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.xs + 2 }}>
              {iconNode}
              {label}
            </span>
          </AppToolTip>
        </ToggleButton>
      );
    })}
  </ToggleButtonGroup>
);

AppViewSwitcher.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node,
      icon: PropTypes.oneOfType([PropTypes.element, PropTypes.elementType]),
      tooltip: PropTypes.node,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func,
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default memo(AppViewSwitcher);
